import { useState } from 'react';
import { Box, Center, Group, Loader, Text, Stack, Button } from '@mantine/core';
import { IconBookmark } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewsService } from '../../services/reviews';
import { ReviewCard } from '../../components/reviews';
import type { Review } from '../../types';

export default function SavedReviews() {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['saved-reviews', page],
    queryFn: () => reviewsService.getSaved(page, 10),
    staleTime: 60_000,
  });

  const allReviews: Review[] = data?.items ?? [];
  const hasMore = data?.hasNextPage ?? false;

  const handleUnsave = (reviewId: string) => {
    queryClient.setQueryData(['saved-reviews', page], (old: typeof data) => {
      if (!old) return old;
      return { ...old, items: old.items.filter((r: Review) => r.id !== reviewId) };
    });
  };

  const handleHelpfulChange = (reviewId: string, helpfulCount: number) => {
    queryClient.setQueryData(['saved-reviews', page], (old: typeof data) => {
      if (!old) return old;
      return {
        ...old,
        items: old.items.map((r: Review) => r.id === reviewId ? { ...r, helpfulCount } : r),
      };
    });
  };


  return (
    <Box>
      {/* Header */}
      <Box
        px="md"
        py="md"
        style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
      >
        <Group gap="xs">
          <IconBookmark size={20} />
          <Text fw={700} size="lg">Saved Reviews</Text>
        </Group>
      </Box>

      {isLoading && allReviews.length === 0 && (
        <Center h={300}><Loader size="md" /></Center>
      )}

      {!isLoading && allReviews.length === 0 && !error && (
        <Center py={60}>
          <Stack align="center" gap="xs">
            <IconBookmark size={32} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed" size="sm">No saved reviews yet.</Text>
            <Text c="dimmed" size="xs">Tap the bookmark on any review to save it here.</Text>
          </Stack>
        </Center>
      )}

      {error && (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <Text c="dimmed">Failed to load saved reviews.</Text>
            <Button variant="subtle" size="xs" onClick={() => queryClient.invalidateQueries({ queryKey: ['saved-reviews', page] })}>Try again</Button>
          </Stack>
        </Center>
      )}

      {allReviews.map((review) => (
        <ReviewCard
          key={review.id}
          review={review}
          showProduct
          onHelpfulChange={handleHelpfulChange}
          onUnsave={handleUnsave}
        />
      ))}

      {!isLoading && hasMore && allReviews.length > 0 && (
        <Center py="md">
          <Button variant="subtle" onClick={() => setPage((p) => p + 1)}>Load more</Button>
        </Center>
      )}
    </Box>
  );
}
